import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, ShoppingCart, ArrowRight, Leaf, ShieldCheck, Heart } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import toast from 'react-hot-toast'; // 👈 Professional Toast Notification Import

export default function Cart() {
  const { cart, updateQuantity, removeFromCart, addToWishlist } = useCart();
  const navigate = useNavigate();

  const subtotal = cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  const shipping = subtotal > 499 ? 0 : 50; 
  const total = subtotal + shipping;
  const totalItemsCount = cart.reduce((acc, item) => acc + item.quantity, 0);

  const handleMoveToWishlist = (item) => {
    if (addToWishlist) {
      addToWishlist(item);
    }
    removeFromCart(item.id);
    toast.success(`${item.name || 'Product'} moved to wishlist!`, {
      icon: '❤️',
    });
  };

  const handleDeleteItem = (itemId, itemName) => {
    removeFromCart(itemId);
    toast.success(`${itemName || 'Item'} removed from cart`, {
      icon: '🗑️',
    });
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-[70vh] bg-gray-100 flex flex-col items-center justify-center px-4">
        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center max-w-md w-full">
          <div className="h-16 w-16 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingCart className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Your Amazon EcoCart is empty</h2>
          <p className="text-xs text-gray-500 mb-6">Check your Saved for later items or continue shopping sustainable products.</p>
          <Link 
            to="/shop" 
            className="inline-block bg-amber-400 hover:bg-amber-500 text-gray-950 px-6 py-2.5 rounded-lg font-bold text-xs shadow transition"
          >
            Shop Today's Eco-Deals
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* Main Cart Items Section */}
        <div className="lg:col-span-3 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-baseline border-b border-gray-200 pb-4 mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Shopping Cart</h1>
            <span className="text-xs text-gray-500">Price</span>
          </div>

          <div className="divide-y divide-gray-200 space-y-4">
            {cart.map((item) => (
              <div key={`${item.id}-${item.selectedSize}-${item.selectedColor}`} className="pt-4 flex flex-col sm:flex-row justify-between items-start gap-4">
                <div className="flex space-x-4 items-start">
                  
                  {/* Dynamic Product Image */}
                  <div className="bg-gray-50 h-24 w-24 rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-200 p-2 overflow-hidden">
                    <img 
                      src={item.image || "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=500"} 
                      alt={item.name} 
                      className="h-full w-full object-contain" 
                    />
                  </div>

                  <div className="space-y-1">
                    <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-semibold uppercase">
                      {item.category || 'Eco-Friendly'}
                    </span>
                    <h3 className="font-bold text-sm text-gray-900 max-w-md">{item.name}</h3>
                    
                    {/* Selected Size and Color Variants */}
                    <div className="text-xs text-gray-600 space-x-2">
                      {item.selectedSize && <span>Size: <strong className="text-gray-900">{item.selectedSize}</strong></span>}
                      {item.selectedColor && <span>| Color: <strong className="text-gray-900">{item.selectedColor}</strong></span>}
                    </div>

                    <p className="text-xs text-emerald-700 font-semibold">In stock</p>
                    <p className="text-[11px] text-gray-500">Eligible for FREE Shipping & Zero-Plastic Packaging</p>

                    {/* Quantity Selector & Actions */}
                    <div className="flex items-center space-x-4 pt-2">
                      <div className="flex items-center border border-gray-300 rounded-md bg-gray-50 shadow-inner">
                        <button 
                          onClick={() => updateQuantity(item.id, -1)}
                          className="px-2.5 py-1 text-gray-700 hover:bg-gray-200 text-xs font-bold"
                        >-</button>
                        <span className="px-3 py-1 font-bold text-xs text-gray-900">{item.quantity}</span>
                        <button 
                          onClick={() => updateQuantity(item.id, 1)}
                          className="px-2.5 py-1 text-gray-700 hover:bg-gray-200 text-xs font-bold"
                        >+</button>
                      </div>

                      <button 
                        onClick={() => handleDeleteItem(item.id, item.name)}
                        className="text-xs font-bold text-cyan-700 hover:underline flex items-center gap-1"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>

                      <span className="text-gray-300">|</span>

                      <button 
                        onClick={() => handleMoveToWishlist(item)}
                        className="text-xs font-bold text-indigo-600 hover:underline flex items-center gap-1"
                      >
                        <Heart className="h-3.5 w-3.5" /> Add to Wishlist
                      </button>
                    </div>
                  </div>
                </div>

                <div className="text-right font-bold text-base text-gray-900 sm:self-start">
                  ₹{item.price * item.quantity}
                </div>
              </div>
            ))}
          </div>

          <div className="text-right pt-6 border-t border-gray-200 mt-6">
            <span className="text-sm text-gray-700">Subtotal ({totalItemsCount} items): </span>
            <span className="text-lg font-black text-gray-900">₹{subtotal}</span>
          </div>
        </div>

        {/* Right Subtotal & Checkout Card */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 h-fit space-y-4">
          <div className="flex items-center space-x-1.5 text-xs text-emerald-700 font-bold bg-emerald-50 p-2.5 rounded-md border border-emerald-200">
            <Leaf className="h-4 w-4 flex-shrink-0" />
            <span>Part of Amazon Green Initiative: {totalItemsCount} Trees Planted!</span>
          </div>

          <div className="text-sm text-gray-800">
            <span>Subtotal ({totalItemsCount} items): </span>
            <span className="font-extrabold text-gray-900">₹{subtotal}</span>
          </div>

          {shipping > 0 && (
            <div className="text-xs text-gray-500">
              Add ₹{500 - subtotal} more of eligible items for FREE Delivery.
            </div>
          )}

          <button 
            onClick={() => navigate('/checkout')}
            className="w-full bg-amber-400 hover:bg-amber-500 text-gray-950 py-2.5 rounded-lg font-bold text-xs shadow transition flex items-center justify-center space-x-1"
          >
            <span>Proceed to Buy</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>

      </div>
    </div>
  );
}